// client/src/components/unidades/sections/UnidadeProfileSection.js
import React, { useState, useCallback, useEffect } from 'react';
import { useMutateUnidades } from '../../../hooks/useUnidades';
import { Save, Loader2, MapPin, XCircle } from 'lucide-react';
import useAlert from '../../../hooks/useAlert';
import UnidadeForm from '../UnidadeForm';
import { lookupCep } from '../../../utils/cepService';

const UnidadeProfileSection = ({ unidade, contextData, onUpdateSuccess, isCreating = false }) => {
    const { setAlert } = useAlert();
    const { updateUnidade } = useMutateUnidades();
    
    // Estado inicial estruturado para evitar TypeError em propriedades aninhadas
    const [formData, setFormData] = useState({
        nome: '',
        codigo: '',
        endereco: {
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            localidade: '',
            uf: '',
            pontoReferencia: ''
        },
        contato: {
            telefone: '',
            telefone2: '',
            whatsapp: '',
            email: '',
            email2: '',
            site: '',
            redesSociais: {
                facebook: '',
                instagram: ''
            }
        },
        geolocalizacao: {
            latitude: '',
            longitude: '',
            enderecoFormatado: ''
        },
        metadata: {
            capacidade: 0,
            areaTotal: 0,
            classificacao: 0,
            possuiEstacionamento: false,
            acessibilidade: false,
            possuiWifi: false,
            tags: '' // no state usamos string, no envio dividimos
        },
        ativo: true
    });
    
    const [errors, setErrors] = useState({});
    const [isEditing, setIsEditing] = useState(isCreating);
    const [isSaving, setIsSaving] = useState(false);
    const [cepEncontrado, setCepEncontrado] = useState(false);

    // Inicializar formulário de forma segura
    useEffect(() => {
        if (unidade) {
            setFormData({
                nome: unidade.nome || '',
                codigo: unidade.codigo || '',
                endereco: {
                    cep: unidade.endereco?.cep || '',
                    logradouro: unidade.endereco?.logradouro || '',
                    numero: unidade.endereco?.numero || '',
                    complemento: unidade.endereco?.complemento || '',
                    bairro: unidade.endereco?.bairro || '',
                    localidade: unidade.endereco?.localidade || '',
                    uf: unidade.endereco?.uf || '',
                    pontoReferencia: unidade.endereco?.pontoReferencia || ''
                },
                contato: {
                    telefone: unidade.contato?.telefone || '',
                    telefone2: unidade.contato?.telefone2 || '',
                    whatsapp: unidade.contato?.whatsapp || '',
                    email: unidade.contato?.email || '',
                    email2: unidade.contato?.email2 || '',
                    site: unidade.contato?.site || '',
                    redesSociais: {
                        facebook: unidade.contato?.redesSociais?.facebook || '',
                        instagram: unidade.contato?.redesSociais?.instagram || ''
                    }
                },
                geolocalizacao: {
                    latitude: unidade.geolocalizacao?.coordinates?.[1] || '',
                    longitude: unidade.geolocalizacao?.coordinates?.[0] || '',
                    enderecoFormatado: unidade.geolocalizacao?.enderecoFormatado || ''
                },
                metadata: {
                    capacidade: unidade.metadata?.capacidade || 0,
                    areaTotal: unidade.metadata?.areaTotal || 0,
                    classificacao: unidade.metadata?.classificacao || 0,
                    possuiEstacionamento: unidade.metadata?.possuiEstacionamento || false,
                    acessibilidade: unidade.metadata?.acessibilidade || false,
                    possuiWifi: unidade.metadata?.possuiWifi !== false,
                    tags: Array.isArray(unidade.metadata?.tags) ? unidade.metadata.tags.join(', ') : ''
                },
                ativo: unidade.ativo !== false
            });
        }
    }, [unidade]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        const finalValue = type === 'checkbox' ? checked : value;
        
        if (name === 'endereco.cep') setCepEncontrado(false);

        // Tratamento para níveis profundos (ex: contato.redesSociais.facebook)
        if (name.includes('.')) {
            const parts = name.split('.');
            
            if (parts.length === 3) {
                const [l1, l2, l3] = parts;
                setFormData(prev => ({
                    ...prev,
                    [l1]: {
                        ...prev[l1],
                        [l2]: {
                            ...prev[l1][l2],
                            [l3]: finalValue
                        }
                    }
                }));
            } else if (parts.length === 2) {
                const [parent, child] = parts;
                setFormData(prev => ({
                    ...prev,
                    [parent]: {
                        ...(prev[parent] || {}),
                        [child]: finalValue
                    }
                }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: finalValue }));
        }
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    }, [errors]);

    const handleCepChange = useCallback(async () => {
        const cep = (formData.endereco?.cep || '').replace(/\D/g, '');
        if (cep.length !== 8) return;
        try {
            const data = await lookupCep(cep);
            setFormData(prev => ({
                ...prev,
                endereco: {
                    ...prev.endereco,
                    logradouro: data.logradouro || prev.endereco?.logradouro || '',
                    bairro:     data.bairro     || prev.endereco?.bairro     || '',
                    localidade: data.localidade || prev.endereco?.localidade || '',
                    uf:         data.uf         || prev.endereco?.uf         || '',
                }
            }));
            setCepEncontrado(true);
            setAlert('CEP encontrado com sucesso!', 'success');
        } catch (error) {
            setAlert(error.message || 'Não foi possível consultar o CEP.', 'danger');
        }
    }, [formData.endereco?.cep, setAlert]);

    const handleSave = async () => {
        const targetUnidadeId = unidade?._id || unidade?.id;
        
        if (!targetUnidadeId) {
            setAlert('ID da unidade não encontrado no sistema', 'error');
            return;
        }

        setIsSaving(true);

        try {
            // Formatar geolocalização se existir
            let geolocalizacaoData = undefined;
            if (formData.geolocalizacao.latitude && formData.geolocalizacao.longitude) {
                geolocalizacaoData = {
                    type: 'Point',
                    coordinates: [
                        parseFloat(formData.geolocalizacao.longitude),
                        parseFloat(formData.geolocalizacao.latitude)
                    ],
                    enderecoFormatado: formData.geolocalizacao.enderecoFormatado || undefined
                };
            }

            // Formatar Tags
            const tagsArray = formData.metadata.tags
                ? formData.metadata.tags.split(',').map(t => t.trim()).filter(Boolean)
                : [];

            // Preparar dados para envio limpos
            const dataToSend = {
                nome: formData.nome?.trim(),
                codigo: formData.codigo?.trim() || '', // Permitir envio vazio no DB caso o cara apague
                endereco: {
                    ...formData.endereco,
                    cep: formData.endereco?.cep?.replace(/\D/g, ''),
                    uf: formData.endereco?.uf?.toUpperCase()
                },
                contato: {
                    ...formData.contato,
                    telefone: formData.contato?.telefone?.replace(/\D/g, ''),
                    telefone2: formData.contato?.telefone2?.replace(/\D/g, ''),
                    whatsapp: formData.contato?.whatsapp?.replace(/\D/g, '')
                },
                geolocalizacao: geolocalizacaoData,
                metadata: {
                    capacidade: Number(formData.metadata?.capacidade) || 0,
                    areaTotal: Number(formData.metadata?.areaTotal) || 0,
                    classificacao: Number(formData.metadata?.classificacao) || 0,
                    possuiEstacionamento: Boolean(formData.metadata?.possuiEstacionamento),
                    acessibilidade: Boolean(formData.metadata?.acessibilidade),
                    possuiWifi: Boolean(formData.metadata?.possuiWifi),
                    tags: tagsArray
                },
                ativo: Boolean(formData.ativo)
            };

            await updateUnidade.mutateAsync({
                unidadeId: targetUnidadeId,
                unidadeData: dataToSend
            });

            setAlert('Unidade atualizada com sucesso!', 'success');
            setIsEditing(false);
            
            if (onUpdateSuccess) {
                onUpdateSuccess();
            }

        } catch (error) {
            console.error('❌ Erro ao salvar unidade:', error);
            setAlert(error.response?.data?.error || 'Erro ao salvar alterações', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (unidade) {
            setFormData({
                nome: unidade.nome || '',
                codigo: unidade.codigo || '',
                endereco: {
                    cep: unidade.endereco?.cep || '',
                    logradouro: unidade.endereco?.logradouro || '',
                    numero: unidade.endereco?.numero || '',
                    complemento: unidade.endereco?.complemento || '',
                    bairro: unidade.endereco?.bairro || '',
                    localidade: unidade.endereco?.localidade || '',
                    uf: unidade.endereco?.uf || '',
                    pontoReferencia: unidade.endereco?.pontoReferencia || ''
                },
                contato: {
                    telefone: unidade.contato?.telefone || '',
                    telefone2: unidade.contato?.telefone2 || '',
                    whatsapp: unidade.contato?.whatsapp || '',
                    email: unidade.contato?.email || '',
                    email2: unidade.contato?.email2 || '',
                    site: unidade.contato?.site || '',
                    redesSociais: {
                        facebook: unidade.contato?.redesSociais?.facebook || '',
                        instagram: unidade.contato?.redesSociais?.instagram || ''
                    }
                },
                geolocalizacao: {
                    latitude: unidade.geolocalizacao?.coordinates?.[1] || '',
                    longitude: unidade.geolocalizacao?.coordinates?.[0] || '',
                    enderecoFormatado: unidade.geolocalizacao?.enderecoFormatado || ''
                },
                metadata: {
                    capacidade: unidade.metadata?.capacidade || 0,
                    areaTotal: unidade.metadata?.areaTotal || 0,
                    classificacao: unidade.metadata?.classificacao || 0,
                    possuiEstacionamento: unidade.metadata?.possuiEstacionamento || false,
                    acessibilidade: unidade.metadata?.acessibilidade || false,
                    possuiWifi: unidade.metadata?.possuiWifi !== false,
                    tags: Array.isArray(unidade.metadata?.tags) ? unidade.metadata.tags.join(', ') : ''
                },
                ativo: unidade.ativo !== false
            });
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const isLoading = isSaving || updateUnidade.isPending;

    return (
        <div className="form-content">
            <div className="form-header">
                <div className="flex-1">
                    <h3 className="form-title">
                        <MapPin size={20} className="inline mr-2"/>
                        Dados da Unidade
                    </h3>
                    
                    {unidade && (
                        <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                unidade.ativo !== false 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-gray-100 text-gray-600'
                            }`}>
                                {unidade.ativo !== false ? 'Ativo' : 'Inativo'}
                            </span>
                            {unidade.codigo && (
                                <span className="text-xs text-gray-500">
                                    Código: {unidade.codigo}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                
                {!isEditing ? (
                    <button 
                        onClick={handleEditClick} 
                        className="action-button action-button-secondary"
                    >
                        ✏️ Editar Unidade
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            onClick={handleCancel}
                            className="action-button action-button-secondary"
                            disabled={isLoading}
                        >
                            <XCircle size={18} className="icon" />
                            Cancelar
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isLoading}
                            className="action-button action-button-primary"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 size={18} className="icon animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <Save size={18} className="icon" />
                                    Salvar Alterações
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <div className="form-tabs-content">
                <UnidadeForm
                    formData={formData}
                    onChange={handleChange}
                    onCepChange={handleCepChange}
                    errors={errors}
                    isEditing={isEditing}
                    isCreating={false}
                    cepEncontrado={cepEncontrado}
                    onUnlockCep={() => setCepEncontrado(false)}
                />
            </div>
        </div>
    );
};

export default UnidadeProfileSection;